import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  ScrollText,
  UserRound,
} from "lucide-react";
import "./timeline-carousel.css";

const PERIOD_ORDER = [
  "Pre-Conquest Period",
  "Claudian Conquest Period",
  "Flavian Dynasty",
  "Trajanic Period",
  "Hadrianic Period",
  "Antonine Period",
  "Severan Period",
  "Crisis of the Third Century.",
  "Diocletianic Period",
  "Constantinian Period",
  "Late Imperial Period",
];

const PERIOD_COPY = {
  "Pre-Conquest Period": {
    range: "55 BC – AD 40",
    strapline:
      "Before conquest, Britain stood beyond Rome’s settled world — known, desired, but not yet held.",
    themes: ["First contact", "Diplomacy", "Tribal kingdoms", "Rome looking west"],
    figures: [
      { name: "Julius Caesar", role: "Roman commander" },
      { name: "Cassivellaunus", role: "Catuvellauni leader" },
      { name: "Tasciovanus", role: "Catuvellauni prince" },
      { name: "Caratacus", role: "Britannic resistance" },
    ],
  },
  "Claudian Conquest Period": {
    range: "AD 43 – 69",
    strapline:
      "The island becomes a province through invasion, resistance, road-building, and revolt.",
    themes: ["Invasion", "Boudica", "Caratacus", "Early Roman rule"],
    figures: [
      { name: "Claudius", role: "Emperor" },
      { name: "Aulus Plautius", role: "Commander" },
      { name: "Caratacus", role: "Resistance leader" },
      { name: "Boudica", role: "Iceni queen" },
    ],
  },
  "Flavian Dynasty": {
    range: "AD 69 – 96",
    strapline:
      "Rome pushes north and west, testing how far conquest can be turned into occupation.",
    themes: ["Agricola", "Wales", "Caledonia", "Frontier ambition"],
    figures: [
      { name: "Vespasian", role: "Emperor" },
      { name: "Agricola", role: "Governor" },
      { name: "Tacitus", role: "Historian" },
      { name: "Calgacus", role: "Caledonian voice" },
    ],
  },
  "Trajanic Period": {
    range: "AD 98 – 117",
    strapline:
      "Britain settles into a harder frontier age while imperial attention turns elsewhere.",
    themes: ["Stanegate", "Consolidation", "Frontier watch", "Imperial distraction"],
    figures: [
      { name: "Trajan", role: "Emperor" },
      { name: "Frontier commanders", role: "Military administration" },
      { name: "Legio IX", role: "Northern mystery" },
      { name: "Provincial governors", role: "Imperial agents" },
    ],
  },
  "Hadrianic Period": {
    range: "AD 117 – 138",
    strapline:
      "The empire draws a line across the north: stone, soldiers, movement, and control.",
    themes: ["Hadrian’s Wall", "Borders", "Military order", "Controlled movement"],
    figures: [
      { name: "Hadrian", role: "Emperor" },
      { name: "Aulus Platorius Nepos", role: "Governor" },
      { name: "Legionary builders", role: "Wall makers" },
      { name: "Frontier communities", role: "Border lives" },
    ],
  },
  "Antonine Period": {
    range: "AD 138 – 193",
    strapline:
      "Rome reaches beyond Hadrian’s Wall, then learns again that the far north cannot easily be held.",
    themes: ["Antonine Wall", "Frontier crisis", "Commodus", "British mutiny"],
    figures: [
      { name: "Antoninus Pius", role: "Emperor" },
      { name: "Lollius Urbicus", role: "Governor" },
      { name: "Commodus", role: "Emperor" },
      { name: "Pertinax", role: "Governor" },
    ],
  },
  "Severan Period": {
    range: "AD 193 – 235",
    strapline:
      "Civil war, imperial anxiety, and northern campaigns reshape Britain’s military and political world.",
    themes: ["Albinus", "Lugdunum", "Severus", "Provincial division"],
    figures: [
      { name: "Septimius Severus", role: "Emperor" },
      { name: "Clodius Albinus", role: "Governor-usurper" },
      { name: "Caracalla", role: "Emperor" },
      { name: "Geta", role: "Co-emperor" },
    ],
  },
  "Crisis of the Third Century.": {
    range: "AD 235 – 285",
    strapline:
      "Britain is drawn into imperial fracture as breakaway powers, plague, and instability disturb Rome’s west.",
    themes: ["Gallic Empire", "Fragmentation", "Coinage", "Military pressure"],
    figures: [
      { name: "Postumus", role: "Gallic emperor" },
      { name: "Gallienus", role: "Emperor" },
      { name: "Aurelian", role: "Restorer" },
      { name: "Tetricus", role: "Gallic ruler" },
    ],
  },
  "Diocletianic Period": {
    range: "AD 284 – 305",
    strapline:
      "Reform and reconquest restore Britain to imperial control after the sea-borne revolt of Carausius.",
    themes: ["Carausius", "Allectus", "Reconquest", "Tetrarchy"],
    figures: [
      { name: "Diocletian", role: "Emperor" },
      { name: "Carausius", role: "British usurper" },
      { name: "Allectus", role: "Successor" },
      { name: "Constantius Chlorus", role: "Caesar" },
    ],
  },
  "Constantinian Period": {
    range: "AD 306 – 378",
    strapline:
      "York becomes a stage for imperial transformation while late Roman Britain faces new pressures.",
    themes: ["Constantine", "Christianity", "Saxon Shore", "Barbarian Conspiracy"],
    figures: [
      { name: "Constantine", role: "Emperor" },
      { name: "Constantius Chlorus", role: "Emperor" },
      { name: "Theodosius", role: "Restorer" },
      { name: "British bishops", role: "Church voices" },
    ],
  },
  "Late Imperial Period": {
    range: "AD 378 – 440s",
    strapline:
      "Roman authority thins, local power hardens, and Britain moves towards a post-imperial world.",
    themes: ["Withdrawal", "Usurpation", "Local defence", "End of Roman rule"],
    figures: [
      { name: "Magnus Maximus", role: "Usurper" },
      { name: "Stilicho", role: "General" },
      { name: "Constantine III", role: "Usurper" },
      { name: "Local rulers", role: "Post-Roman power" },
    ],
  },
};

function cleanPeriodName(period = "") {
  return period.replace(/\.$/, "");
}

function normaliseText(value = "") {
  return String(value)
    .toLowerCase()
    .replace(/[.,;:!?\"'’‘“”]/g, "")
    .trim();
}


function figureImagePath(name = "") {
  const imageMap = {
    // =========================
    // PRE-CONQUEST
    // =========================
    "Julius Caesar": "/images/timeline/figures/julius-caesar.webp",
    Cassivellaunus: "/images/timeline/figures/cassivellaunus.webp",
    Tasciovanus: "/images/timeline/figures/tasciovanus.webp",
    Caratacus: "/images/timeline/figures/caratacus.webp",

    // =========================
    // CLAUDIAN
    // =========================
    Claudius: "/images/timeline/figures/claudius.webp",
    "Aulus Plautius": "/images/timeline/figures/aulus-plautius.webp",
    Boudica: "/images/timeline/figures/boudica.webp",

    // =========================
    // FLAVIAN
    // =========================
    Vespasian: "/images/timeline/figures/vespasian.webp",
    Agricola: "/images/timeline/figures/agricola.webp",
    Tacitus: "/images/timeline/figures/tacitus.webp",
    Calgacus: "/images/timeline/figures/calgacus.webp",

    // =========================
    // TRAJANIC
    // =========================
    Trajan: "/images/timeline/figures/trajan.webp",
"Frontier commanders":
  "/images/timeline/figures/frontier-commander.webp",

"Legio IX":
  "/images/timeline/figures/legio-ix.webp",

"Provincial governors":
  "/images/timeline/figures/provincial-governor.webp",
    // =========================
    // HADRIANIC
    // =========================
    Hadrian: "/images/timeline/figures/hadrian.webp",
    "Aulus Platorius Nepos":
      "/images/timeline/figures/aulus-platorius-nepos.webp",
"Legionary builders":
  "/images/timeline/figures/legionary-builder.webp",

"Frontier communities":
  "/images/timeline/figures/frontier-community.webp",

    // =========================
    // ANTONINE
    // =========================
    "Antoninus Pius":
      "/images/timeline/figures/antoninus-pius.webp",

    "Lollius Urbicus":
      "/images/timeline/figures/lollius-urbicus.webp",

    Commodus: "/images/timeline/figures/commodus.webp",

    Pertinax: "/images/timeline/figures/pertinax.webp",

    "Marcus Aurelius":
      "/images/timeline/figures/marcus-aurelius.webp",

    // =========================
    // SEVERAN
    // =========================
    "Septimius Severus":
      "/images/timeline/figures/septimius-severus.webp",

    "Clodius Albinus":
      "/images/timeline/figures/clodius-albinus.webp",

    Caracalla: "/images/timeline/figures/caracalla.webp",

    Geta: "/images/timeline/figures/geta.webp",

    // =========================
    // CRISIS
    // =========================
    Postumus: "/images/timeline/figures/postumus.webp",

    Gallienus: "/images/timeline/figures/gallienus.webp",

    Aurelian: "/images/timeline/figures/aurelian.webp",

    Tetricus: "/images/timeline/figures/tetricus.webp",

    // =========================
    // DIOCLETIANIC
    // =========================
    Diocletian: "/images/timeline/figures/diocletian.webp",

    Carausius: "/images/timeline/figures/carausius.webp",

    Allectus: "/images/timeline/figures/allectus.webp",

    "Constantius Chlorus":
      "/images/timeline/figures/constantius-chlorus.webp",

    // =========================
    // CONSTANTINIAN
    // =========================
    Constantine: "/images/timeline/figures/constantine.webp",

    "Constantine the Great":
      "/images/timeline/figures/constantine.webp",

    Theodosius: "/images/timeline/figures/theodosius.webp",
"British bishops":
  "/images/timeline/figures/british-bishop.webp",

    // =========================
    // LATE IMPERIAL
    // =========================
    "Magnus Maximus":
      "/images/timeline/figures/magnus-maximus.webp",

    Stilicho: "/images/timeline/figures/stilicho.webp",

    "Constantine III":
      "/images/timeline/figures/constantine-iii.webp",
"Local rulers":
  "/images/timeline/figures/local-ruler.webp",
  };

  return imageMap[name] || "";
}

function figureProfilePath(name = "") {
  const profileMap = {
    "Julius Caesar": "/content/key-figures/julius-caesar.md",
    Cassivellaunus: "/content/key-figures/cassivellaunus.md",
    Tasciovanus: "/content/key-figures/tasciovanus.md",
    Caratacus: "/content/key-figures/caratacus.md",

    Claudius: "/content/key-figures/claudius.md",
    "Aulus Plautius": "/content/key-figures/aulus-plautius.md",
    Boudica: "/content/key-figures/boudica.md",

    Vespasian: "/content/key-figures/vespasian.md",
    Agricola: "/content/key-figures/agricola.md",
    Tacitus: "/content/key-figures/tacitus.md",
    Calgacus: "/content/key-figures/calgacus.md",

    Trajan: "/content/key-figures/trajan.md",
    "Frontier commanders": "/content/key-figures/frontier-commanders.md",
    "Legio IX": "/content/key-figures/legio-ix.md",
    "Provincial governors": "/content/key-figures/provincial-governors.md",

    Hadrian: "/content/key-figures/hadrian.md",
    "Aulus Platorius Nepos": "/content/key-figures/aulus-platorius-nepos.md",
    "Legionary builders": "/content/key-figures/legionary-builders.md",
    "Frontier communities": "/content/key-figures/frontier-communities.md",

    "Antoninus Pius": "/content/key-figures/antoninus-pius.md",
    "Lollius Urbicus": "/content/key-figures/lollius-urbicus.md",
    Commodus: "/content/key-figures/commodus.md",
    Pertinax: "/content/key-figures/pertinax.md",
    "Marcus Aurelius": "/content/key-figures/marcus-aurelius.md",

    "Septimius Severus": "/content/key-figures/septimius-severus.md",
    "Clodius Albinus": "/content/key-figures/clodius-albinus.md",
    Caracalla: "/content/key-figures/caracalla.md",
    Geta: "/content/key-figures/geta.md",

    Postumus: "/content/key-figures/postumus.md",
    Gallienus: "/content/key-figures/gallienus.md",
    Aurelian: "/content/key-figures/aurelian.md",
    Tetricus: "/content/key-figures/tetricus.md",

    Diocletian: "/content/key-figures/diocletian.md",
    Carausius: "/content/key-figures/carausius.md",
    Allectus: "/content/key-figures/allectus.md",
    "Constantius Chlorus": "/content/key-figures/constantius-chlorus.md",

    Constantine: "/content/key-figures/constantine.md",
    "Constantine the Great": "/content/key-figures/constantine.md",
    Theodosius: "/content/key-figures/theodosius.md",
    "British bishops": "/content/key-figures/british-bishops.md",

    "Magnus Maximus": "/content/key-figures/magnus-maximus.md",
    Stilicho: "/content/key-figures/stilicho.md",
    "Constantine III": "/content/key-figures/constantine-iii.md",
    "Local rulers": "/content/key-figures/local-rulers.md",
  };

  return profileMap[name] || "";
}

function groupByPeriod(items) {
  const map = new Map();

  items.forEach((item) => {
    const period = item.period || "Unassigned Period";
    if (!map.has(period)) map.set(period, []);
    map.get(period).push(item);
  });

  return Array.from(map.entries())
    .sort(([a], [b]) => {
      const ai = PERIOD_ORDER.indexOf(a);
      const bi = PERIOD_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    })
    .map(([period, events]) => ({
      period,
      events,
      image: events.find((event) => event.image)?.image || "",
      ...(PERIOD_COPY[period] || {
        range: events[0]?.year || "Roman Britain",
        strapline: "A turning point in the long story of Roman Britain.",
        themes: ["Empire", "Frontier", "Province", "Change"],
        figures: [
          { name: "Roman governor", role: "Provincial power" },
          { name: "Local elites", role: "Civic leadership" },
          { name: "Soldiers", role: "Military presence" },
          { name: "Communities", role: "Daily life" },
        ],
      }),
    }));
}

export default function RomanTimelineScroll() {
  const [timelineData, setTimelineData] = useState([]);
  const [periodIndex, setPeriodIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [openEventId, setOpenEventId] = useState(null);

  useEffect(() => {
    fetch("/timeline/timeline-data.json")
      .then((res) => res.json())
      .then((data) => setTimelineData(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Error loading timeline data:", err));
  }, []);

  const periods = useMemo(() => groupByPeriod(timelineData), [timelineData]);
  const current = periods[periodIndex];
  const progress = current ? `${periodIndex + 1} / ${periods.length}` : "";

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === "ArrowLeft") goPrevious();
      if (event.key === "ArrowRight") goNext();
      if (event.key === "Escape") {
        setFlipped(false);
        setOpenEventId(null);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods.length]);

  function goPrevious() {
    if (!periods.length) return;
    setFlipped(false);
    setOpenEventId(null);
    setPeriodIndex((value) => (value === 0 ? periods.length - 1 : value - 1));
  }

  function goNext() {
    if (!periods.length) return;
    setFlipped(false);
    setOpenEventId(null);
    setPeriodIndex((value) => (value === periods.length - 1 ? 0 : value + 1));
  }

  function toggleFlip() {
    setFlipped((value) => !value);
    setOpenEventId(null);
  }

  if (!current) {
    return <div className="timeline-loading">Loading timeline…</div>;
  }

  return (
    <main className="timeline-carousel-page">
      <div
        className="timeline-carousel-bg"
        style={{ backgroundImage: current.image ? `url(${current.image})` : undefined }}
      />
      <div className="timeline-carousel-scrim" />
      <div className="timeline-ambient" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>

      <section className="timeline-carousel-shell" aria-label="Roman Britannia Timeline">
        <header className="timeline-carousel-header">
          <p className="timeline-kicker">Roman Britannia Timeline</p>
          <h1>{cleanPeriodName(current.period)}</h1>
          <p>{current.range || "Roman Britain"}</p>
        </header>

        <div className="timeline-carousel-stage">
          <button className="timeline-nav-btn left" onClick={goPrevious} aria-label="Previous period">
            <ArrowLeft size={22} />
          </button>

          <article className={`period-flip-card ${flipped ? "is-flipped" : ""}`}>
            <div className="period-flip-inner">
              <div className="period-face period-front">
                <div className="period-meta-row">
                  <span>{progress}</span>
                  <span>{current.events.length} entries</span>
                </div>

                <div className="period-card-body">
                  <div className="period-front-main">
                    <p className="period-label">Historical Period</p>
                    <p className="period-range">{current.range}</p>
                    <p className="period-strapline">{current.strapline}</p>

                    <div className="period-themes">
                      {(current.themes || []).map((theme) => (
                        <span key={theme}>{theme}</span>
                      ))}
                    </div>
                  </div>

                  <div className="period-divider" />

                  <section className="period-key-figures" aria-label="Key figures of this period">
                    <p className="period-label">Key Figures of this Period</p>
                    <div className="period-figure-grid">
                      {(current.figures || []).map((figure) => {
                        const imagePath = figure.image || figureImagePath(figure.name);
                        const profilePath = figure.profile || figureProfilePath(figure.name);

                        const cardContent = (
                          <>
                            <div className="period-figure-portrait">
                              {imagePath ? (
                                <img src={imagePath} alt={figure.name} loading="lazy" />
                              ) : (
                                <UserRound size={30} aria-hidden="true" />
                              )}
                            </div>
                            <h3>{figure.name}</h3>
                            <p>{figure.role}</p>
                          </>
                        );

                        return profilePath ? (
                          <a
                            className="period-figure-card period-figure-link"
                            href={`/key-figures/${figure.name
                              .toLowerCase()
                              .replace(/\s+/g, "-")}`}
                            key={`${current.period}-${figure.name}`}
                            aria-label={`Open profile for ${figure.name}`}
                          >
                            {cardContent}
                          </a>
                        ) : (
                          <article className="period-figure-card" key={`${current.period}-${figure.name}`}>
                            {cardContent}
                          </article>
                        );
                      })}
                    </div>
                  </section>
                </div>

                <button className="period-primary-btn" onClick={toggleFlip}>
                  <RotateCcw size={18} />
                  Explore this period
                </button>
              </div>

              <div className="period-face period-back">
                <div className="period-back-header">
                  <div>
                    <p className="period-label">Events & Articles</p>
                    <h2>{cleanPeriodName(current.period)}</h2>
                  </div>
                  <button className="period-icon-btn" onClick={toggleFlip} aria-label="Return to period cover">
                    <RotateCcw size={18} />
                  </button>
                </div>

                <div className="period-event-list">
                  {current.events.map((event) => {
                    const isOpen = openEventId === event.id;
                    const hasDetails = Array.isArray(event.details) && event.details.length > 0;
                    const hasArticle = event.article?.enabled && event.article?.path;
                    const showTitle =
                      event.title && normaliseText(event.title) !== normaliseText(event.text);

                    return (
                      <section className="period-event" key={event.id}>
                        <div className="period-event-topline">
                          <span>{event.year}</span>
                          {hasArticle && <span className="article-pill">Article</span>}
                        </div>

                        {showTitle && <h3>{event.title}</h3>}
                        <p>{event.text}</p>

                        <div className="period-event-actions">
                          {hasDetails && (
                            <button
                              className="period-small-btn"
                              onClick={() => setOpenEventId(isOpen ? null : event.id)}
                            >
                              {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                              Read more
                            </button>
                          )}

                          {hasArticle && (
                            <a className="period-small-btn" href={event.article.path}>
                              <BookOpen size={16} />
                              {event.article.label || "Full article"}
                            </a>
                          )}
                        </div>

                        {isOpen && hasDetails && (
                          <div className="period-event-details">
                            {event.details.map((detail, index) => (
                              <p key={`${event.id}-detail-${index}`}>{detail}</p>
                            ))}
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              </div>
            </div>
          </article>

          <button className="timeline-nav-btn right" onClick={goNext} aria-label="Next period">
            <ArrowRight size={22} />
          </button>
        </div>

        <footer className="timeline-period-strip" aria-label="Timeline period selector">
          {periods.map((period, index) => (
            <button
              key={period.period}
              className={index === periodIndex ? "active" : ""}
              onClick={() => {
                setPeriodIndex(index);
                setFlipped(false);
                setOpenEventId(null);
              }}
            >
              <ScrollText size={14} />
              {cleanPeriodName(period.period)}
            </button>
          ))}
        </footer>
      </section>
    </main>
  );
}
